/**
 * @fileoverview URL State Manager - Single source of truth for shareable app state.
 * @module UrlState
 *
 * CONTEXT: Central hub for all URL-based state (view, filters, sort, etc.)
 * UPSTREAM: Called by App, FilterModule, ListViewModule when state changes
 * DOWNSTREAM: Updates browser URL, notifies subscribers on popstate
 * FAILURE: Invalid params are ignored, defaults used by calling modules
 */
const UrlState = (function() {

  //======== PRIVATE STATE ========

  /** @type {Function[]} Callbacks to fire on popstate (back/forward navigation) */
  const changeCallbacks = [];


  //======== CORE FUNCTIONS ========

  /**
   * Gets a single URL parameter value.
   * @param {string} key - The parameter name to retrieve
   * @returns {string|null} The parameter value, or null if not present
   *
   * @example
   * // URL: ?view=list&bedrooms=3
   * UrlState.get('view');     // 'list'
   * UrlState.get('bedrooms'); // '3'
   * UrlState.get('missing');  // null
   */
  function get(key) {
    return new URLSearchParams(window.location.search).get(key);
  }

  /**
   * Gets all URL parameters as an object.
   * @returns {Object.<string, string>} Key-value pairs of all URL parameters
   *
   * @example
   * // URL: ?view=list&status=rent
   * UrlState.getAll(); // { view: 'list', status: 'rent' }
   */
  function getAll() {
    const params = new URLSearchParams(window.location.search);
    const result = {};
    params.forEach((value, key) => {
      result[key] = value;
    });
    return result;
  }

  /**
   * Sets one or more URL parameters and updates browser history.
   * @param {Object.<string, string|null>} newParams - Parameters to set (null removes param)
   * @param {Object} [options={}] - Options
   * @param {boolean} [options.replace=false] - Use replaceState instead of pushState
   *
   * @example
   * // Set single param (creates history entry)
   * UrlState.set({ view: 'list' });
   *
   * // Set multiple params at once
   * UrlState.set({ view: 'list', status: 'rent' });
   *
   * // Remove a param
   * UrlState.set({ status: null });
   *
   * // Replace current history entry (no back button)
   * UrlState.set({ view: 'map' }, { replace: true });
   */
  function set(newParams, options = {}) {
    const params = new URLSearchParams(window.location.search);

    // Update params - set or delete based on value
    Object.entries(newParams).forEach(([key, value]) => {
      if (value === null || value === undefined) {
        params.delete(key);
      } else {
        params.set(key, String(value));
      }
    });

    // Build new URL (handle empty params case - no trailing ?)
    const queryString = params.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    // Update browser history
    if (options.replace) {
      history.replaceState(newParams, '', newUrl);
    } else {
      history.pushState(newParams, '', newUrl);
    }
  }

  /**
   * Removes one or more URL parameters.
   * @param {string|string[]} keys - Parameter name(s) to remove
   * @param {Object} [options={}] - Options
   * @param {boolean} [options.replace=false] - Use replaceState instead of pushState
   *
   * @example
   * UrlState.remove('status');
   * UrlState.remove(['status', 'bedrooms']);
   */
  function remove(keys, options = {}) {
    const keysArray = Array.isArray(keys) ? keys : [keys];
    const params = new URLSearchParams(window.location.search);

    keysArray.forEach(key => params.delete(key));

    // Build new URL
    const queryString = params.toString();
    const newUrl = queryString
      ? `${window.location.pathname}?${queryString}`
      : window.location.pathname;

    // Update browser history
    if (options.replace) {
      history.replaceState({}, '', newUrl);
    } else {
      history.pushState({}, '', newUrl);
    }
  }

  /**
   * Subscribes to URL state changes (browser back/forward navigation).
   * @param {Function} callback - Called with (state, event) on popstate
   *
   * @example
   * UrlState.onChange(function(state, event) {
   *   console.log('URL changed to:', state);
   *   if (state.view !== currentView) {
   *     switchView(state.view);
   *   }
   * });
   */
  function onChange(callback) {
    changeCallbacks.push(callback);
  }


  //======== EVENT LISTENERS ========

  /**
   * Handle browser back/forward navigation.
   * Notifies all subscribers with the new URL state.
   */
  window.addEventListener('popstate', function(event) {
    const state = getAll();
    changeCallbacks.forEach(cb => cb(state, event));
  });


  //======== VALIDATION HELPERS ========
  //
  // SECURITY NOTE: All URL parameters are user-controlled input and MUST be validated
  // before use. These helpers provide safe parsing with:
  // - Type coercion with fallback to defaults
  // - Range clamping for numeric values
  // - Whitelist validation for enum values
  // - Length limits for string values
  // - Array size limits for list values
  //
  // NEVER insert raw URL values directly into the DOM - always use these validators
  // or equivalent sanitization. See CLAUDE.md for security guidelines.
  //

  /**
   * Validates and parses an enum value from URL.
   * SECURITY: Only accepts values from the allowed whitelist.
   *
   * @param {string} key - URL parameter name
   * @param {string[]} allowedValues - Whitelist of valid values
   * @param {string} defaultValue - Fallback if invalid/missing
   * @returns {string} Validated value or default
   *
   * @example
   * // URL: ?status=rent
   * UrlState.getEnum('status', ['buy', 'rent'], 'buy'); // 'rent'
   * // URL: ?status=invalid
   * UrlState.getEnum('status', ['buy', 'rent'], 'buy'); // 'buy' (default)
   */
  function getEnum(key, allowedValues, defaultValue) {
    const value = get(key);
    if (value !== null && allowedValues.includes(value)) {
      return value;
    }
    return defaultValue;
  }

  /**
   * Validates and parses a number from URL with range clamping.
   * SECURITY: Ensures numeric type and clamps to valid range.
   *
   * @param {string} key - URL parameter name
   * @param {number} defaultValue - Fallback if invalid/missing
   * @param {number} [min=-Infinity] - Minimum allowed value
   * @param {number} [max=Infinity] - Maximum allowed value
   * @returns {number} Validated and clamped number
   *
   * @example
   * // URL: ?bedrooms=3
   * UrlState.getNumber('bedrooms', 0, 0, 4); // 3
   * // URL: ?bedrooms=999
   * UrlState.getNumber('bedrooms', 0, 0, 4); // 4 (clamped to max)
   * // URL: ?bedrooms=abc
   * UrlState.getNumber('bedrooms', 0, 0, 4); // 0 (default)
   */
  function getNumber(key, defaultValue, min = -Infinity, max = Infinity) {
    const value = get(key);
    if (value === null) {
      return defaultValue;
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return defaultValue;
    }
    return Math.min(max, Math.max(min, num));
  }

  /**
   * Validates and parses a string from URL with length limit.
   * SECURITY: Limits string length to prevent memory exhaustion attacks.
   *
   * @param {string} key - URL parameter name
   * @param {string} defaultValue - Fallback if missing
   * @param {number} [maxLength=100] - Maximum allowed length
   * @returns {string} Validated string (truncated if too long)
   *
   * @example
   * // URL: ?realtor=abc-realty
   * UrlState.getString('realtor', '', 50); // 'abc-realty'
   */
  function getString(key, defaultValue, maxLength = 100) {
    const value = get(key);
    if (value === null) {
      return defaultValue;
    }
    // Truncate to max length for safety
    return value.substring(0, maxLength);
  }

  /**
   * Validates and parses a comma-separated list of strings from URL.
   * SECURITY: Limits array size and individual item length.
   *
   * @param {string} key - URL parameter name
   * @param {string[]} defaultValue - Fallback if missing
   * @param {string[]} [allowedValues=null] - Optional whitelist (null = allow all)
   * @param {number} [maxItems=20] - Maximum array size
   * @returns {string[]} Validated array of strings
   *
   * @example
   * // URL: ?type=home,apartment,lot
   * UrlState.getArray('type', [], ['home', 'apartment', 'lot', 'commercial']);
   * // Returns: ['home', 'apartment', 'lot']
   */
  function getArray(key, defaultValue, allowedValues = null, maxItems = 20) {
    const value = get(key);
    if (value === null || value === '') {
      return defaultValue;
    }

    let items = value.split(',')
      .map(s => s.trim())
      .filter(s => s.length > 0)
      .slice(0, maxItems); // Limit array size

    // If whitelist provided, filter to only allowed values
    if (allowedValues !== null) {
      items = items.filter(item => allowedValues.includes(item));
    }

    return items.length > 0 ? items : defaultValue;
  }

  /**
   * Validates and parses a comma-separated list of numbers from URL.
   * SECURITY: Ensures all values are valid numbers within range.
   *
   * @param {string} key - URL parameter name
   * @param {number[]} defaultValue - Fallback if missing
   * @param {number} [min=-Infinity] - Minimum allowed value per item
   * @param {number} [max=Infinity] - Maximum allowed value per item
   * @param {number} [maxItems=50] - Maximum array size
   * @returns {number[]} Validated array of numbers
   *
   * @example
   * // URL: ?amenities=1,3,5,7
   * UrlState.getNumberArray('amenities', [], 0, 100); // [1, 3, 5, 7]
   */
  function getNumberArray(key, defaultValue, min = -Infinity, max = Infinity, maxItems = 50) {
    const value = get(key);
    if (value === null || value === '') {
      return defaultValue;
    }

    const items = value.split(',')
      .map(s => parseFloat(s.trim()))
      .filter(n => !isNaN(n) && n >= min && n <= max)
      .slice(0, maxItems);

    return items.length > 0 ? items : defaultValue;
  }

  /**
   * Validates and parses geo bounds from URL (sw_lat,sw_lng,ne_lat,ne_lng format).
   * SECURITY: Validates all 4 values are valid lat/lng coordinates.
   *
   * @param {string} key - URL parameter name
   * @returns {Object|null} Bounds object {sw: {lat, lng}, ne: {lat, lng}} or null
   *
   * @example
   * // URL: ?bounds=12.0,-69.2,12.4,-68.7
   * UrlState.getBounds('bounds');
   * // Returns: { sw: { lat: 12.0, lng: -69.2 }, ne: { lat: 12.4, lng: -68.7 } }
   */
  function getBounds(key) {
    const value = get(key);
    if (value === null) {
      return null;
    }

    const parts = value.split(',').map(s => parseFloat(s.trim()));
    if (parts.length !== 4 || parts.some(isNaN)) {
      return null;
    }

    const [swLat, swLng, neLat, neLng] = parts;

    // Validate lat/lng ranges
    if (swLat < -90 || swLat > 90 || neLat < -90 || neLat > 90) {
      return null;
    }
    if (swLng < -180 || swLng > 180 || neLng < -180 || neLng > 180) {
      return null;
    }

    return {
      sw: { lat: swLat, lng: swLng },
      ne: { lat: neLat, lng: neLng }
    };
  }


  //======== PUBLIC API ========

  return {
    get: get,
    getAll: getAll,
    set: set,
    remove: remove,
    onChange: onChange,
    // Validation helpers (SECURITY: use these instead of raw get() for filter state)
    getEnum: getEnum,
    getNumber: getNumber,
    getString: getString,
    getArray: getArray,
    getNumberArray: getNumberArray,
    getBounds: getBounds
  };

})();

// Expose globally
window.UrlState = UrlState;
